import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { ReccuiringData } from '@/interfaces/reccuringData';
import { initialUserAccount, ItermediateAccount } from '@/interfaces/account';
import { NewBeneficiary } from '@/interfaces/beneficiar';
import { formatAccountNumber } from '@/utils/accountNumberFormater';
import { IFile } from '@/interfaces/attachment';

interface NewPaymentState {
  beneficiaryData: NewBeneficiary;
  reccuringData: ReccuiringData;
  saveBeneficiary: boolean;
  fee: string;
  account: ItermediateAccount;
  reason: string;
  amount: string;
  isBeneficiaryEdited: boolean;
  isBenInAdressBook: boolean;
  attachments: IFile[];
}

const initialState: NewPaymentState = {
  beneficiaryData: {
    name: '',
    recipientName: '',
    firstName: '',
    lastName: '',
    accountNumber: '',
    bankName: '',
    isInternal: true,
    branch: '',
    phone: '',
    email: '',
    country: '',
    currency: '',
    town: '',
    address: '',
    zipCode: '',
    iban: '',
    swift: '',
  },
  reccuringData: {
    period: 'MONTHLY',
    startDate: '',
    recurringInvoiceName: '',
    endDate: '',
  },
  amount: '',
  reason: '',
  account: initialUserAccount,
  fee: '0.00',
  saveBeneficiary: false,
  isBeneficiaryEdited: false,
  isBenInAdressBook: false,
  attachments: [],
};

export const newPaymentSlice = createSlice({
  name: 'newPayment',
  initialState,
  reducers: {
    handleBenChange: (state, action: PayloadAction<{ key: string; newValue: any }>) => {
      const key = action.payload.key;
      const newValue = action.payload.newValue;
      if (!state.isBeneficiaryEdited && key !== 'accountNumber') {
        state.isBeneficiaryEdited = true;
      }

      if (key === 'accountNumber') {
        const formattedValue = formatAccountNumber(newValue);
        state.beneficiaryData = { ...state.beneficiaryData, [key]: formattedValue };
      } else {
        state.beneficiaryData = { ...state.beneficiaryData, [key]: newValue };
      }
    },
    handleFullBenChange: (state, action: PayloadAction<NewBeneficiary>) => {
      state.beneficiaryData = action.payload;
      state.isBeneficiaryEdited = false;
    },
    handleSaveBeneficiary: (state, action: PayloadAction<boolean>) => {
      state.saveBeneficiary = action.payload;
    },
    handleBenInAdressBook: (state, action: PayloadAction<boolean>) => {
      state.isBenInAdressBook = action.payload;
    },
    handleSetAccount: (state, action: PayloadAction<ItermediateAccount>) => {
      state.account = action.payload;
    },
    setPayment: (state, action: PayloadAction<{ response: any; fee: string; currAccount: any }>) => {
      const response = action.payload.response;

      state.reason = response.reason;
      state.amount = response.amount;
      state.fee = action.payload.fee;
      state.account = action.payload.currAccount;
      state.beneficiaryData = {
        ...state.beneficiaryData,
        isInternal: response.isInternal,
        bankName: response.beneficiary.bankName,
        branch: response.beneficiary.branchCode,
        town: response.beneficiary.town,
        address: response.beneficiary.address,
        zipCode: response.beneficiary.zipCode,
        phone: response.beneficiary.phoneNumber,
        email: response.beneficiary.email,
        currency: action.payload.currAccount?.account.currency.alphabeticCode,
        country: response.beneficiary.country.name,
        recipientName: response.beneficiary.name,
        accountNumber: response.beneficiary.accountNumber,
      };
    },

    handleReccuringChange: (state, action: PayloadAction<{ key: string; newValue: string }>) => {
      const key = action.payload.key;
      const newValue = action.payload.newValue;
      switch (key) {
        case 'amount':
          state.amount = newValue;
          return;
        case 'reason':
          state.reason = newValue;
          return;
        case 'fee':
          state.fee = newValue;
          return;
        default:
          state.reccuringData = { ...state.reccuringData, [key]: newValue };
      }
    },
    handleClearData: () => initialState,
    handleSetAttachments: (state, action: PayloadAction<IFile[]>) => {
      state.attachments = action.payload;
    },
  },
});

export const {
  setPayment,
  handleBenChange,
  handleReccuringChange,
  handleSetAccount,
  handleClearData,
  handleFullBenChange,
  handleSaveBeneficiary,
  handleBenInAdressBook,
  handleSetAttachments,
} = newPaymentSlice.actions;

export const selectNewPayment = (state: RootState) => state.newPayment;

export default newPaymentSlice.reducer;
